import { useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

// internal hooks
import { useEscape } from "../../hooks/useEscape";
import { useGetCsvData } from "../../hooks/useGetCsvData";
import { useVerifyUnique } from "../../hooks/useVerifyUnique";

// styles and images
import "../project/Project.css";
import "./VendorInvoices.css";
import Close from "../../assets/close.svg";

// global
import {
  userStore,
  dbDataStore,
  selectOptionsStore,
  projectStore,
  messageAndErrorStore,
} from "../../store/Store";

// components
import InputField from "../../formComponents/InputField";
import FormButton from "../../formComponents/FormButton";

export default function AddVendorInvoice({ setTriggerInvoice }) {
  // global
  const currentUser = userStore((data) => data.currentUser);
  const JwtToken = userStore((data) => data.JwtToken);
  const currentCompany = userStore((data) => data.currentCompany);
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const currentProject = projectStore((data) => data.currentProject);
  const projectVendorInvoices = projectStore(
    (data) => data.projectVendorInvoices
  );
  const setProjectVendorInvoices = projectStore(
    (data) => data.setProjectVendorInvoices
  );
  const setProjectVendorInvoiceTotals = projectStore(
    (data) => data.setProjectVendorInvoiceTotals
  );
  const projectLoads = projectStore((data) => data.projectLoads);
  const projectCarriers = projectStore((data) => data.projectCarriers);
  const setProjectLoads = projectStore((data) => data.setProjectLoads);
  const setProjectAssetLoads = projectStore(
    (data) => data.setProjectAssetLoads
  );
  const companyData = dbDataStore((data) => data.companyData);
  const carrierOptions = selectOptionsStore((data) => data.carrierOptions);
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)

  const navigate = useNavigate();
  const [vendorDD, setVendorDD] = useState([]);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceLoads, setInvoiceLoads] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [pending, setPending] = useState(false);
  const [vendorInvoice, setVendorInvoice] = useState({
    id: 0,
    project_id: currentProject.project.id,
    invoice_name: "",
    company_id: currentCompany.id,
    vendor_name: "",
    is_sand: false,
    is_asset: false,
    purchase_order: "",
    created_by_id: currentUser.id,
    updated_by_id: currentUser.id,
    customer_invoice_id: 0,
    discount_percent: 0,
    vendor_id: 0,
    pc_carrier_id: "",
  });

  // get vendor dropdown
  useEffect(() => {
    const dropdown = [];
    if (currentProject && companyData) {
      const company = companyData.filter(
        (x) => x.id === currentProject.project.vendor_id
      );
      if (company.length > 0) {
        dropdown.push({
          value: currentProject.project.vendor_id,
          label: company[0].company_name,
        });
      }
    }
    if (carrierOptions && projectCarriers) {
      carrierOptions.forEach((c) => {
        const addedCarrier = projectCarriers.filter(
          (x) => x.pc_carrier_id === c.value && !x.is_pass_through
        );
        if (addedCarrier.length === 1) {
          dropdown.push({ value: c.value, label: c.label });
        }
      });
    }
    setVendorDD(dropdown);
  }, [
    currentProject,
    companyData,
    carrierOptions,
    setVendorDD,
    projectCarriers,
  ]);

  // parse out the file
  useGetCsvData(invoiceFile, setInvoiceLoads);
  const { exists } = useVerifyUnique(
    projectVendorInvoices.invoices,
    "invoice_name",
    vendorInvoice.invoice_name
  );

  const handleClose = () => {
    setTriggerInvoice(false);
  };

  useEscape(setTriggerInvoice, [], [], pending);

  const handleName = (e) => {
    setVendorInvoice({
      ...vendorInvoice,
      invoice_name: e.target.value,
    });
  };

  const handleVendor = (e) => {
    setVendor(e);
    if (typeof e.value === "string") {
      setVendorInvoice({
        ...vendorInvoice,
        vendor_name: e.label,
        pc_carrier_id: e.value,
      });
    } else {
      setVendorInvoice({
        ...vendorInvoice,
        vendor_name: e.label,
        vendor_id: e.value,
      });
    }
  };
  const handleFile = (e) => {
    let f = e.target.files[0];
    if (!f.type.includes("text/csv")) {
      toggleNewMessage("Please use the customer invoice template csv file");
      return;
    }
    setInvoiceFile(f);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPending(true);
    if (exists) {
      toggleNewMessage("That invoice name already exists");
      setPending(false);
      return;
    }
    // update load data
    const loads = [];
    for (var i = 1; i < invoiceLoads.length; i++) {
      if (invoiceLoads[0].errors.length > 0) {
        toggleNewMessage("There was an issue parsing out the uploaded file");
        setPending(false);
        return;
      } else if (invoiceLoads[i].errors.length > 0) {
        toggleNewMessage("There was an issue parsing out the uploaded file");
        setPending(false);
        return;
      }
      // get the load id from project loads
      const load_no = parseInt(invoiceLoads[i].data[0]);

      const projectLoad = projectLoads.filter((x) => x.load_no === load_no);
      if (projectLoad.length === 0) {
        loads.push({
          load_no: load_no,
          weight_tons: parseFloat(invoiceLoads[i].data[3]),
          moisture_content: 0,
          load_id: 0,
          mileage: 0,
          carrier_name: "",
          pc_carrier_id: "",
          pc_driver_id: "",
          driver_name: "",
          po_number: "",
          product_name: "",
          terminal_name: "",
          ticket_number: "",
          delivered_date: "2012-02-21T08:02:00-07:00",
          freight_cost: parseFloat(invoiceLoads[i].data[1]),
          freight_rev: 0,
          fsc_cost: parseFloat(invoiceLoads[i].data[2]),
          fsc_rev: 0,
          sand_cost: 0,
          sand_rev: 0,
          load_is_missing: true,
          created_by_id: currentUser.id,
          updated_by_id: currentUser.id,
        });
      } else {
        loads.push({
          load_no: load_no,
          weight_tons: parseFloat(invoiceLoads[i].data[3]),
          moisture_content: 0,
          load_id: projectLoad[0].id,
          mileage: projectLoad[0].approved_mileage,
          carrier_name: projectLoad[0].carrier_name,
          pc_carrier_id: projectLoad[0].pc_carrier_id,
          pc_driver_id: projectLoad[0].pc_driver_id,
          driver_name: projectLoad[0].driver_name,
          terminal_name: projectLoad[0].terminal_name,
          po_number: projectLoad[0].po_number,
          product_name: projectLoad[0].product_name,
          ticket_number: projectLoad[0].ticket_number,
          delivered_date: projectLoad[0].delivered_date,
          freight_cost: parseFloat(invoiceLoads[i].data[1]),
          freight_rev: projectLoad[0].freight_rev,
          fsc_cost: parseFloat(invoiceLoads[i].data[2]),
          fsc_rev: projectLoad[0].fsc_rev,
          sand_cost: 0,
          sand_rev: 0,
          load_is_missing: false,
          duplicate_of: projectLoad[0].vendor_invoice_id,
          created_by_id: currentUser.id,
          updated_by_id: currentUser.id,
        });
      }
    }
    const reqBody = {
      invoice: vendorInvoice,
      loads: loads,
      project_id: currentProject.project.id,
    };
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);

    const requestOptions = {
      method: "POST",
      headers,
      body: JSON.stringify(reqBody),
      signal: controller.signal,
    };

    fetch(
      `${process.env.REACT_APP_BACKEND}/pc/vendor-invoice/${vendorInvoice.id}`,
      requestOptions
    )
      .then((res) => {
        if (res.status === 401) {
          navigate("/login");
          setAuthStatus(false);
          setPending(false);
          setTriggerInvoice(false);
          return;
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (data.error) {
          if (data.message === "project is archived and cannot be updated") {
            toggleNewMessage("Project is Archived and cannot be updated");
            setPending(false);
            return;
          } else {
            toggleNewMessage("Something went wrong");
            console.log(data);
            setPending(false);
            return;
          }
        } else {
          toggleNewMessage(null, "Invoice Added");
          setProjectVendorInvoices(data.invoice_data);
          setProjectLoads(data.project_loads);
          setProjectAssetLoads(data.project_asset_loads);
          setProjectVendorInvoiceTotals(data.vendor_invoice_totals);
          setPending(false);
          setTriggerInvoice(false);
        }
      })
      .catch((err) => {
        console.log(err);
        toggleNewMessage("Something went wrong");
        setPending(false);
        return;
      })
      .finally(() => setPending(false));
    return () => controller.abort();
  };

  return (
    <div className="popup">
      <div className="popup-inner">
        {!pending && (
          <div className="close">
            <img onClick={handleClose} src={Close} alt="close" />
          </div>
        )}
        <h3>Add Vendor Invoice</h3>
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <InputField
              name={"invoice_name"}
              type={"text"}
              title={"Name"}
              placeholder={"Invoice Name"}
              onChange={handleName}
              value={vendorInvoice.invoice_name}
              required={true}
            />
          </div>
          <div className="row g-3">
            <span>Vendor</span>
            <Select
              id="vendor"
              options={vendorDD}
              onChange={handleVendor}
              placeholder="Select Vendor"
              value={vendor}
              className="select-box"
            />
          </div>
          <div className="row g-3">
            <InputField
              name={"invoice_file"}
              type={"file"}
              title={"Vendor Backup"}
              placeholder={"Choose File"}
              onChange={handleFile}
              required={true}
            />
          </div>
          <div className="row g-3">
            <div className="col">
              <FormButton name={"Submit"} pending={pending} />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
